/**
 * PATH: apps/billing/src/pages/ApprovalsPage.tsx
 * FILE_ID: BILLING-PAGE-APPROVALS-001
 * PURPOSE: Queue of bills awaiting human approval (status === 'extracted').
 * WHY: Was rendering a hard-coded 3-row mock list. Now live-wired to
 *      /api/bills/?status=extracted via useBills() with one-click approve/reject.
 *
 * FLOW:
 *   useBills({ status: 'extracted' })  -> the queue
 *   useBillStats()                     -> KPI tiles (today's approvals, value)
 *   approve.mutate({ billId })         -> POST /api/bills/{id}/approve/
 *   reject.mutate({ billId, reason })  -> POST /api/bills/{id}/reject/
 *
 * RELATED:
 *   docs/architecture/billing/00_MASTER_PLAN.md § Phase A
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  User,
  DollarSign,
  Loader2,
  AlertTriangle,
  RefreshCcw,
} from 'lucide-react';
import { formatCurrency, formatDate, cn } from '@/lib/utils';
import {
  useBills,
  useBillStats,
  useApproveBill,
  useRejectBill,
  type BillListRow,
} from '../hooks/useBills';

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

function parseAmount(v: string | null | undefined): number | null {
  if (v == null) return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const s = Math.round(ms / 1000);
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min${m === 1 ? '' : 's'} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

/** Heuristic priority: failed extraction or due-soon = high; >$5000 = medium; else low. */
function priorityFor(bill: BillListRow): 'low' | 'medium' | 'high' {
  if (bill.status === 'failed') return 'high';
  if (bill.due_date) {
    const days = Math.round(
      (new Date(bill.due_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
    );
    if (days <= 3) return 'high';
    if (days <= 14) return 'medium';
  }
  const amount = parseAmount(bill.total);
  if (amount != null && amount > 5000) return 'medium';
  return 'low';
}

const priorityConfig = {
  low: { label: 'Low', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
  medium: {
    label: 'Medium',
    color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  },
  high: {
    label: 'High',
    color: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ApprovalsPage() {
  const { data, isLoading, error, refetch, isFetching } = useBills({ status: 'extracted' });
  const { data: stats } = useBillStats();
  const approve = useApproveBill();
  const reject = useRejectBill();

  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const approvals = data?.bills ?? [];
  const totalValue = approvals.reduce((sum, b) => {
    const a = parseAmount(b.total);
    return a != null ? sum + a : sum;
  }, 0);

  // Stats for the top tiles. `pushed_24h` is what we have closest to "approved
  // today" in the existing API contract — once the backend exposes a
  // dedicated approved-today count we'll switch.
  const pushed24h = stats?.processing?.pushed_24h ?? 0;

  const handleApprove = (id: string) => approve.mutate({ billId: id });
  const handleSubmitReject = (id: string) => {
    reject.mutate(
      { billId: id, reason: rejectReason },
      {
        onSuccess: () => {
          setRejectingId(null);
          setRejectReason('');
        },
      },
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Approvals</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {approvals.length} bill{approvals.length === 1 ? '' : 's'} awaiting your approval
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          aria-label="Refresh approvals"
        >
          <RefreshCcw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Stats tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatTile
          label="Pending"
          value={approvals.length}
          icon={Clock}
          colorClass="bg-amber-100 dark:bg-amber-900/40 text-amber-600"
        />
        <StatTile
          label="Pushed (24h)"
          value={pushed24h}
          icon={CheckCircle}
          colorClass="bg-green-100 dark:bg-green-900/40 text-green-600"
        />
        <StatTile
          label="Total value"
          value={formatCurrency(totalValue)}
          icon={DollarSign}
          colorClass="bg-blue-100 dark:bg-blue-900/40 text-blue-600"
        />
      </div>

      {/* List */}
      {isLoading ? (
        <div className="p-12 flex items-center justify-center text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading approvals…
        </div>
      ) : error ? (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-6 text-center">
          <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-red-600 dark:text-red-400">
            Couldn't load approvals: {error.message}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-3 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      ) : approvals.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-12 text-center">
          <CheckCircle className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
          <p className="text-gray-700 dark:text-gray-300 font-medium">All caught up</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No bills awaiting approval right now.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {approvals.map((bill) => {
            const priority = priorityFor(bill);
            const amount = parseAmount(bill.total);
            const isRejecting = rejectingId === bill.id;
            return (
              <div
                key={bill.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-4 min-w-0">
                    <div className="h-12 w-12 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link
                          to={`/bills/${bill.id}`}
                          className="font-semibold text-gray-900 dark:text-white hover:text-emerald-600 truncate"
                        >
                          {bill.vendor_name || bill.email_from || '(unknown vendor)'}
                        </Link>
                        <span
                          className={cn(
                            'px-2 py-0.5 rounded-full text-xs font-medium',
                            priorityConfig[priority].color,
                          )}
                        >
                          {priorityConfig[priority].label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {bill.invoice_number ? `Invoice #${bill.invoice_number}` : 'No invoice number'}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-sm text-gray-500 dark:text-gray-400 flex-wrap">
                        <span className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          {bill.email_from || 'unknown'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {relativeTime(bill.extracted_at || bill.created_at)}
                        </span>
                        {bill.due_date && <span>Due: {formatDate(bill.due_date)}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Amount</p>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">
                        {amount != null ? formatCurrency(amount) : '—'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setRejectingId(isRejecting ? null : bill.id)}
                        className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 dark:border-red-600 dark:hover:bg-red-900/20"
                      >
                        <XCircle className="h-4 w-4" />
                        Reject
                      </button>
                      <button
                        onClick={() => handleApprove(bill.id)}
                        disabled={approve.isPending && approve.variables?.billId === bill.id}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {approve.isPending && approve.variables?.billId === bill.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <CheckCircle className="h-4 w-4" />
                        )}
                        Approve
                      </button>
                    </div>
                  </div>
                </div>

                {isRejecting && (
                  <div className="mt-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 space-y-2">
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Reason for rejection (optional)"
                      className="w-full p-2 text-sm border border-red-300 dark:border-red-700 rounded bg-white dark:bg-gray-800"
                      rows={2}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSubmitReject(bill.id)}
                        disabled={reject.isPending}
                        className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                      >
                        {reject.isPending ? 'Rejecting…' : 'Confirm reject'}
                      </button>
                      <button
                        onClick={() => {
                          setRejectingId(null);
                          setRejectReason('');
                        }}
                        className="px-3 py-1.5 text-sm border border-red-300 dark:border-red-700 rounded text-red-700 dark:text-red-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {approve.isError && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
          Approve failed: {approve.error.message}
        </div>
      )}
      {reject.isError && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
          Reject failed: {reject.error.message}
        </div>
      )}
    </div>
  );
}

function StatTile({
  label,
  value,
  icon: Icon,
  colorClass,
}: {
  label: string;
  value: React.ReactNode;
  icon: typeof Clock;
  colorClass: string;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
      <div className="flex items-center gap-4">
        <div className={cn('h-12 w-12 rounded-lg flex items-center justify-center', colorClass)}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}
